/**
 * 売上・原価の簡易手入力(暫定)。
 *
 * decisions.md 2026-08-20:
 *  - 4段階の承認フローを通さず、**確定(confirmed)として直接登録**する
 *  - 直接入力できるのは `accounts.pl_line` が 'revenue' または 'cogs' の科目のみ
 *    (`expense` の科目は必ず申請→承認→提出→確定を通る。セキュリティルールで担保)
 *  - 誤入力の訂正のため、管理者に限り確定後の修正・削除ができる
 *
 * 運送はフェーズ3でRoutine自動連携に置き換わる前提の暫定入力。
 */
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { fiscalYearOf, toMonth } from '@core/lib/date';
import { isValidAmount } from '@core/lib/money';
import { writeAuditLog } from '@core/lib/auditLog';
import type { Account, DateStr, DepartmentId, Transaction, Yen } from '@core/lib/types';

const COLLECTION = 'transactions';

function toTransaction(snap: QueryDocumentSnapshot): Transaction {
  return { id: snap.id, ...(snap.data() as object) } as Transaction;
}

export type DirectEntryInput = {
  /** 所属会社(既定はSUM) */
  companyId: string;
  departmentId: DepartmentId;
  accountId: string;
  subAccountId: string;
  hierarchyNodeId: string | null;
  postingDate: DateStr;
  amount: Yen;
  counterparty: string;
  description: string;
};

/** 直接入力できる科目か(売上・原価のみ) */
export function isDirectEntryAccount(account: Account | undefined): boolean {
  return account?.pl_line === 'revenue' || account?.pl_line === 'cogs';
}

function assertValid(input: DirectEntryInput, accounts: Account[]) {
  if (!isValidAmount(input.amount)) throw new Error('金額は1円以上の整数で入力してください');
  if (!input.accountId || !input.subAccountId) throw new Error('科目①と科目②を選んでください');
  if (!input.postingDate) throw new Error('計上日を入力してください');
  const account = accounts.find((a) => a.id === input.accountId);
  if (!isDirectEntryAccount(account)) {
    throw new Error('この科目は直接入力できません(経費は申請・承認のフローを通してください)');
  }
}

export async function createDirectEntry(params: {
  input: DirectEntryInput;
  accounts: Account[];
  userId: string;
  fiscalStartMonth: number;
}): Promise<void> {
  const { input, accounts, userId, fiscalStartMonth } = params;
  assertValid(input, accounts);

  const account = accounts.find((a) => a.id === input.accountId);
  const id = doc(collection(getDb(), COLLECTION)).id;
  const now = serverTimestamp();

  await setDoc(doc(getDb(), COLLECTION, id), {
    // 売上は income、原価は expense(お金の向き)。承認フローは通さない
    type: account?.pl_line === 'revenue' ? 'income' : 'expense',
    status: 'paid',
    code: null,
    doc_type: null,
    payer: null,
    vendor_id: null,
    invoice_no: null,
    invoice_date: null,
    company_id: input.companyId,
    department_id: input.departmentId,
    account_id: input.accountId,
    sub_account_id: input.subAccountId,
    hierarchy_node_id: input.hierarchyNodeId,

    amount: input.amount,
    applied_amount: input.amount,
    tax_mode: 'inclusive',
    tax_amount: null,

    posting_date: input.postingDate,
    posting_month: toMonth(input.postingDate),
    // 売上・原価の入金/支払予定はCF側で別途登録する。ここでは計上日と同じにしておく
    payment_date: input.postingDate,
    payment_month: toMonth(input.postingDate),
    fiscal_year: fiscalYearOf(input.postingDate, fiscalStartMonth),
    due_date: null,

    counterparty: input.counterparty || null,
    description: input.description || null,
    payment_method: null,
    evidence_type: null,

    attachments: [],
    receipts: [],

    is_internal: false,
    counterparty_department_id: null,

    approval_rule_id: '',
    approval_snapshot: [],
    current_step: 0,
    approvals: [],

    applied_at: null,
    applied_by: null,
    manager_checked_at: null,
    manager_checked_by: null,
    approved_at: null,
    approved_by: null,
    paid_at: now,
    paid_by: userId,
    completed_at: null,
    completed_by: null,
    submitted_at: null,
    submitted_by: null,
    confirmed_at: null,
    confirmed_by: null,
    rejected_at: null,
    rejected_by: null,
    reject_reason: null,
    rejected_from_status: null,

    source: 'manual',
    cash_entry_id: null,
    recurring_cost_id: null,

    created_by: userId,
    created_at: now,
    updated_by: userId,
    updated_at: now,
  });

  await writeAuditLog({
    targetType: 'transaction',
    targetId: id,
    action: 'direct-entry-create',
    by: userId,
    after: {
      department_id: input.departmentId,
      account_id: input.accountId,
      amount: input.amount,
      posting_date: input.postingDate,
    },
  });
}

/** 管理者のみ。暫定入力の訂正 */
export async function updateDirectEntry(params: {
  id: string;
  input: DirectEntryInput;
  accounts: Account[];
  userId: string;
  fiscalStartMonth: number;
}): Promise<void> {
  const { id, input, accounts, userId, fiscalStartMonth } = params;
  assertValid(input, accounts);

  await updateDoc(doc(getDb(), COLLECTION, id), {
    account_id: input.accountId,
    sub_account_id: input.subAccountId,
    hierarchy_node_id: input.hierarchyNodeId,
    department_id: input.departmentId,
    amount: input.amount,
    applied_amount: input.amount,
    posting_date: input.postingDate,
    posting_month: toMonth(input.postingDate),
    payment_date: input.postingDate,
    payment_month: toMonth(input.postingDate),
    fiscal_year: fiscalYearOf(input.postingDate, fiscalStartMonth),
    counterparty: input.counterparty || null,
    description: input.description || null,
    updated_by: userId,
    updated_at: serverTimestamp(),
  });

  await writeAuditLog({
    targetType: 'transaction',
    targetId: id,
    action: 'direct-entry-update',
    by: userId,
    after: { amount: input.amount, posting_date: input.postingDate },
  });
}

/** 管理者のみ。誤入力の削除 */
export async function deleteDirectEntry(id: string, userId: string): Promise<void> {
  await deleteDoc(doc(getDb(), COLLECTION, id));
  await writeAuditLog({
    targetType: 'transaction',
    targetId: id,
    action: 'direct-entry-delete',
    by: userId,
  });
}

/** 直接入力ぶんの一覧(事業部×月)。経費は含めない */
export async function fetchDirectEntries(
  departmentId: DepartmentId,
  month: string,
  accounts: Account[],
  max = 200,
): Promise<Transaction[]> {
  const snap = await getDocs(
    query(
      collection(getDb(), COLLECTION),
      where('department_id', '==', departmentId),
      where('posting_month', '==', month),
      where('status', '==', 'paid'),
      orderBy('posting_date', 'desc'),
      limit(max),
    ),
  );
  const directIds = new Set(accounts.filter((a) => isDirectEntryAccount(a)).map((a) => a.id));
  return snap.docs.map(toTransaction).filter((t) => directIds.has(t.account_id));
}
